import React, { ChangeEvent, RefObject } from 'react';
import { ArrowRightIcon, CheckCircle2Icon, ClockIcon, FileUpIcon, ShieldCheckIcon } from 'lucide-react';
import { CarliChat } from './CarliChat';
import { activePrompts, activityFor, attentionFor, onboardingPrompts, recommendationFor } from '../data/homeSignals';
export type PortalView = 'carli' | 'inventory' | 'performance' | 'marketing' | 'controls' | 'messages' | 'finances' | 'demand' | 'pipeline';
type QuickStats = {
  received: string;
  sold: string;
  sales: string;
  live: string;
  maxOpportunity: string;
};
type HomeDashboardProps = {
  selectedOwner: string;
  quickStats: QuickStats;
  hasInventory: boolean;
  shopifyConnected: boolean;
  onConnectShopify: () => void;
  fileInputRef: RefObject<HTMLInputElement>;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  fileName: string | null;
  onRunAnalysis: () => void;
  onNavigate: (view: PortalView) => void;
  latestReport?: {
    name: string;
    onOpen: () => void;
  };
};
export function HomeDashboard({
  selectedOwner,
  quickStats,
  hasInventory,
  shopifyConnected,
  onConnectShopify,
  fileInputRef,
  onFileChange,
  fileName,
  onRunAnalysis,
  onNavigate,
  latestReport
}: HomeDashboardProps) {
  const attention = hasInventory ? attentionFor(selectedOwner) : [];
  const recommendation = hasInventory ? recommendationFor(selectedOwner) : null;
  const activity = hasInventory ? activityFor(selectedOwner) : [];
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" data-id="element-906" data-mp="1dn6ypv17uecp">
      <section className="border-b border-ink/10 pb-8" aria-labelledby="home-heading" data-id="element-907" data-mp="19mwno3ykgxqz">
        <CarliLabel data-id="element-908" data-mp="14njxpacefak6" />
        <h1 id="home-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-909" data-mp="14mr0p138rzf8">
          {hasInventory ? 'Your recovery overview' : 'Welcome to Carli'}
        </h1>
        <p className="mt-3 max-w-2xl text-lg leading-relaxed text-ink/65" data-id="element-910" data-mp="3peaqq0pezyyt">
          {hasInventory ? 'See what’s happening, what Carli recommends, and what needs your attention.' : 'Start by adding inventory. Carli will evaluate the opportunity and recommend the best recovery path.'}
        </p>
      </section>

      {hasInventory ? <>
          <AttentionSection items={attention} liveUnits={quickStats.live} onNavigate={onNavigate} data-id="element-911" data-mp="3cv5nh11xdp7x" />

          <section className="mt-6" aria-labelledby="snapshot-heading" data-id="element-912" data-mp="3hu8ehhl2luww">
            <div className="flex items-center justify-between gap-4" data-id="element-913" data-mp="3hsfbcc9o1stn">
              <h2 id="snapshot-heading" className="text-xs font-semibold uppercase tracking-wider text-ink/45" data-id="element-914" data-mp="334kdyikpr0a8">Recovery snapshot</h2>
              {latestReport && <button type="button" onClick={latestReport.onOpen} className="inline-flex items-center gap-1.5 text-sm font-medium text-forest hover:underline" data-id="element-915" data-mp="2u57feky4nw4a">
                  Latest report: {latestReport.name}
                  <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-916" data-mp="2k684evpw3oe4" />
                </button>}
            </div>
            <dl className="mt-3 grid gap-px overflow-hidden rounded-2xl border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-5" data-id="element-917" data-mp="2p4t50w920a33">
              <Snapshot label="Active units" value={quickStats.live} data-id="element-918" data-mp="20ky52vkastjt" />
              <Snapshot label="Units sold" value={quickStats.sold} data-id="element-919" data-mp="20msscgj1kxr2" />
              <Snapshot label="Sell-through" value={sellThrough(quickStats)} data-id="element-920" data-mp="2fiqjtli0tdwy" />
              <Snapshot label="Recovery generated" value={quickStats.sales} data-id="element-921" data-mp="2aimcakz3btiv" />
              <Snapshot label="Current opportunity" value={quickStats.maxOpportunity} data-id="element-922" data-mp="2oidmeamhkprl" />
            </dl>
          </section>

          {recommendation && <section className="mt-6 overflow-hidden rounded-3xl bg-ink text-cream shadow-sm" aria-labelledby="recommendation-heading" data-id="element-923" data-mp="2jj0xlkc218md">
              <div className="grid gap-6 p-6 sm:p-9 lg:grid-cols-[1.4fr_0.6fr] lg:items-end" data-id="element-924" data-mp="2t9dg770b6rpv">
                <div data-id="element-925" data-mp="3794rgzzfxu06">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cream/55" data-id="element-926" data-mp="378myqdbzlxx0">
                    <CarliGlyph tone="cream" data-id="element-927" data-mp="338updyerrnql" />
                    Carli recommends
                  </span>
                  <h2 id="recommendation-heading" className="mt-4 max-w-2xl font-display text-3xl font-medium tracking-tight sm:text-4xl" data-id="element-928" data-mp="10kcssrqzmq4b">
                    {recommendation.title}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-cream/65" data-id="element-929" data-mp="1abh85skksczo">{recommendation.observation}</p>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-cream/75" data-id="element-930" data-mp="1fblg2u7f3zea">{recommendation.reasoning}</p>
                </div>
                <div className="lg:text-right" data-id="element-931" data-mp="mkbhsjh0e2b4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cream/50" data-id="element-932" data-mp="mn7g48wrqt03">Expected impact</p>
                  <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-933" data-mp="rmkb5gxsn6dr">{recommendation.impact}</p>
                  <button type="button" onClick={() => onNavigate(recommendation.target)} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#BB5E40] px-5 py-3 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-934" data-mp="22zmswfbmq1zi">
                    {recommendation.action}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-935" data-mp="3nji6zyncpczl" />
                  </button>
                </div>
              </div>
            </section>}
        </> : <section className="mt-8" aria-labelledby="getting-started-heading" data-id="element-936" data-mp="3dphwfh9avfjh">
          <h2 id="getting-started-heading" className="sr-only" data-id="element-937" data-mp="38u2kiuuurtpp">Getting started</h2>
          <ol className="grid gap-4 lg:grid-cols-3" data-id="element-938" data-mp="33sd40zov2nu8">
            <Step number="1" title="Add inventory" text="Upload a spreadsheet or connect Shopify." data-id="element-939" data-mp="2zrt9m3vo9ybs" />
            <Step number="2" title="Set your recovery rules" text="Choose approved channels, pricing boundaries, and timing." data-id="element-940" data-mp="2usq4kjfozs3t" />
            <Step number="3" title="Review Carli’s recommendation" text="Carli will estimate recovery and recommend a recovery strategy." data-id="element-941" data-mp="y0pxfa64wbp4" />
          </ol>
        </section>}

      <section className="mt-6 grid gap-5 lg:grid-cols-[1.05fr_0.95fr]" aria-labelledby="add-inventory-heading" data-id="element-942" data-mp="1c68f8s4x0iyl">
        <article className="rounded-3xl border border-ink/10 bg-paper p-6 shadow-sm sm:p-8" data-id="element-943" data-mp="1gx8cjyhy531m">
          <h2 id="add-inventory-heading" className="font-display text-2xl font-medium tracking-tight sm:text-3xl" data-id="element-944" data-mp="135tqfisqslqc">Add inventory</h2>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-ink/65" data-id="element-945" data-mp="1856eu7z9i0v1">Give Carli new inventory to evaluate and recover.</p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-946" data-mp="1vruxivw3hxcp">
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFileChange} className="sr-only" aria-label="Upload inventory file" data-id="element-947" data-mp="1zu9ibljbr76m" />
            <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center justify-center gap-2 rounded-full bg-forest px-5 py-3.5 text-sm font-semibold text-cream transition-colors hover:bg-forest-dark" data-id="element-948" data-mp="1quw8usv7ailw">
              <FileUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-949" data-mp="7vy87w2eqyq8" />
              Upload inventory
            </button>
            <button type="button" onClick={onConnectShopify} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#95BF47] px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#7FAE3A]" data-id="element-950" data-mp="2xwqbegpx5gc">
              {shopifyConnected ? <CheckCircle2Icon className="h-4 w-4" aria-hidden="true" data-id="element-951" data-mp="3kcttt8dc7gh6" /> : <ShopifyGlyph data-id="element-952" data-mp="3kb8znjuxpzju" />}
              {shopifyConnected ? 'Shopify connected' : 'Connect Shopify'}
            </button>
          </div>

          {fileName && <div className="mt-4 flex items-center gap-2 rounded-xl border border-forest/20 bg-forest/5 px-4 py-3 text-sm text-ink/70" role="status" data-id="element-953" data-mp="3u4zl0fvrltvb">
              <CheckCircle2Icon className="h-4 w-4 shrink-0 text-forest" aria-hidden="true" data-id="element-954" data-mp="31mlt0wx41245" />
              <span data-id="element-955" data-mp="2woah8gn98hja"><strong className="font-semibold text-ink" data-id="element-956" data-mp="2wokb4fbf78u9">{fileName}</strong> is ready to add to your recovery workspace.</span>
            </div>}

          <p className="mt-6 flex items-start gap-2 border-t border-ink/10 pt-5 text-xs leading-relaxed text-ink/50" data-id="element-957" data-mp="2c40g4sg7d11z">
            <ShieldCheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-forest" aria-hidden="true" data-id="element-958" data-mp="2g5luki8dxnea" />
            Nothing is activated without your approval. ERP and 3PL connections are coming next.
          </p>
        </article>

        <div className="overflow-hidden rounded-3xl shadow-sm" data-id="element-959" data-mp="2v1l0bm80vbg9">
          <CarliChat prompts={hasInventory ? activePrompts : onboardingPrompts} subheading="Ask about your inventory, recovery strategy, performance, or what to do next." context={carliContext(selectedOwner, quickStats, hasInventory)} data-id="element-960" data-mp="2uz8lvj1h3j7y" />
        </div>
      </section>

      <section className="mt-5 rounded-3xl border border-[#BB5E40]/25 bg-[#BB5E40]/5 p-5 sm:p-7" aria-labelledby="test-inventory-heading" data-id="element-961" data-mp="3drj6txw68whs">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-962" data-mp="3irp2rq9h555o">
          <div data-id="element-963" data-mp="174buirzvq58p">
            <h2 id="test-inventory-heading" className="text-xl font-semibold tracking-tight" data-id="element-964" data-mp="174vfyioqnbrh">Test inventory with Carli</h2>
            <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-ink/65" data-id="element-965" data-mp="126aekfxr2m1g">
              Explore the recovery opportunity before adding inventory to your account. Upload a spreadsheet and Carli will estimate potential recovery, likely channels, and expected timing.
            </p>
            <p className="mt-1.5 text-xs text-ink/50" data-id="element-966" data-mp="1pt0cb532fug9">Nothing will be added to your live inventory or activated without your approval.</p>
          </div>
          <button type="button" onClick={onRunAnalysis} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-[#BB5E40] px-5 py-3 text-sm font-semibold text-[#94452E] transition-colors hover:bg-[#BB5E40] hover:text-cream" data-id="element-967" data-mp="1g3gwk8anggpm">
            Run Carli Analysis
          </button>
        </div>
      </section>

      {activity.length > 0 && <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 sm:p-7" aria-labelledby="activity-heading" data-id="element-968" data-mp="fqqfh2gepchs">
          <div className="flex items-center justify-between gap-4" data-id="element-969" data-mp="bq6ipzzqwaw6">
            <h2 id="activity-heading" className="flex items-center gap-2 text-base font-semibold" data-id="element-970" data-mp="pp65t37m0hq8">
              <ClockIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-971" data-mp="ko0hdhkzfgmi" />
              Recent activity
            </h2>
            <button type="button" onClick={() => onNavigate('performance')} className="text-sm font-medium text-forest hover:underline" data-id="element-972" data-mp="yn058n0o7rhm">
              View all activity
            </button>
          </div>
          <ul className="mt-4 divide-y divide-ink/10" data-id="element-973" data-mp="18gqnha67f5ov">
            {activity.map(entry => <li key={`${entry.when}-${entry.detail}`} className="flex flex-col gap-0.5 py-3 sm:flex-row sm:items-baseline sm:gap-6" data-id="element-974" data-mp="13hmg8p2ubi16">
                <span className="w-24 shrink-0 text-xs font-semibold uppercase tracking-wider text-ink/45" data-id="element-975" data-mp="1ddz8rjmnfpuc">{entry.when}</span>
                <span className="text-sm text-ink/75" data-id="element-976" data-mp="1x0kc9kplluga">{entry.detail}</span>
              </li>)}
          </ul>
        </section>}
    </div>;
}
function AttentionSection({
  items,
  liveUnits,
  onNavigate
}: {
  items: ReturnType<typeof attentionFor>;
  liveUnits: string;
  onNavigate: (view: PortalView) => void;
}) {
  if (items.length === 0) {
    return <section className="mt-8 rounded-3xl border border-forest/20 bg-forest/5 p-5 sm:p-7" aria-labelledby="attention-heading" data-id="element-977" data-mp="1dbrz1l33yjyj">
        <div className="flex items-start gap-3" data-id="element-978" data-mp="1i3a0ea91mmk1">
          <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-forest" aria-hidden="true" data-id="element-979" data-mp="1i4322wt5alvb" />
          <div data-id="element-980" data-mp="1490khcexr82o">
            <h2 id="attention-heading" className="text-lg font-semibold" data-id="element-981" data-mp="199onkwgfvfd9">You’re all caught up</h2>
            <p className="mt-1 text-sm leading-relaxed text-ink/65" data-id="element-982" data-mp="ucxl2ebgxnjf">
              Carli is actively managing {liveUnits} units across your approved recovery channels.
            </p>
          </div>
        </div>
      </section>;
  }
  return <section className="mt-8" aria-labelledby="attention-heading" data-id="element-983" data-mp="g7x6we1oiwrw">
      <div className="flex items-center gap-2.5" data-id="element-984" data-mp="bfvp6clqsduh">
        <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#BB5E40]" data-id="element-985" data-mp="bf41rp2atmfa" />
        <h2 id="attention-heading" className="text-lg font-semibold" data-id="element-986" data-mp="6hu6vtw54ik2">Needs your attention</h2>
        <span className="rounded-full bg-[#BB5E40]/12 px-2.5 py-0.5 text-xs font-semibold text-[#94452E]" data-id="element-987" data-mp="1jk8zbj173ur">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </div>

      <ul className="mt-3 space-y-3" data-id="element-988" data-mp="u8819r276tht">
        {items.map(item => <li key={item.id} data-id="element-989" data-mp="p7ahxb5gbteq">
            <article className="flex flex-col gap-4 rounded-2xl border border-[#BB5E40]/25 bg-paper p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6" data-id="element-990" data-mp="p68x4eymvsjt">
              <div data-id="element-991" data-mp="39ykicoahrnt5">
                <h3 className="text-base font-semibold" data-id="element-992" data-mp="39z3z41oeo65l">{item.title}</h3>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink/65" data-id="element-993" data-mp="34z7z0ditwyvb">{item.detail}</p>
                {item.impact && <p className="mt-2 text-sm font-semibold text-forest" data-id="element-994" data-mp="1khozcxdnpg3j">{item.impact}</p>}
              </div>
              <button type="button" onClick={() => onNavigate(item.target)} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-995" data-mp="1ph1wqc25m5kd">
                {item.action}
              </button>
            </article>
          </li>)}
      </ul>
    </section>;
}
function Snapshot({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="bg-paper px-5 py-5" data-id="element-996" data-mp="2d64edjwsfyev">
      <dt className="text-xs font-semibold uppercase tracking-wider text-ink/45" data-id="element-997" data-mp="2i4flhmvp7ute">{label}</dt>
      <dd className="mt-1.5 font-display text-2xl font-semibold tracking-tight text-ink" data-id="element-998" data-mp="irjwfezuqgz6">{value}</dd>
    </div>;
}
function Step({
  number,
  title,
  text
}: {
  number: string;
  title: string;
  text: string;
}) {
  return <li className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" data-id="element-999" data-mp="2e78qy21ejtge">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#BB5E40]/12 font-display text-sm font-semibold text-[#94452E]" data-id="element-1000" data-mp="298nrw5xvzftj">{number}</span>
      <h3 className="mt-4 text-base font-semibold" data-id="element-1001" data-mp="2n7wuu3x30dfk">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-ink/65" data-id="element-1002" data-mp="2j6boz2iyszev">{text}</p>
    </li>;
}
function CarliLabel() {
  return <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1003" data-mp="1vi26a9h1srry">
      <CarliGlyph data-id="element-1004" data-mp="1qjqtbpvgi15i" />
      Carli
    </span>;
}
function CarliGlyph({
  tone = 'coral'
}: {
  tone?: 'coral' | 'cream';
}) {
  return <span aria-hidden="true" className={`h-4 w-4 shrink-0 ${tone === 'cream' ? 'bg-cream' : 'bg-[#BB5E40]'}`} style={{
    WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
    maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain'
  }} data-id="element-1005" data-mp="1zivul3yek8ld" />;
}
function ShopifyGlyph() {
  return <span aria-hidden="true" className="h-4 w-4 shrink-0 bg-cream" style={{
    WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/7LeZM2476qhffRRTPqij7m/shopify_glyph_black.svg)',
    maskImage: 'url(https://cdn.magicpatterns.com/uploads/7LeZM2476qhffRRTPqij7m/shopify_glyph_black.svg)',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain'
  }} data-id="element-1006" data-mp="soerwh446l2w" />;
}
function sellThrough({
  received,
  sold
}: QuickStats) {
  const receivedUnits = Number(received.replace(/,/g, ''));
  const soldUnits = Number(sold.replace(/,/g, ''));
  if (!receivedUnits) return '—';
  return `${(soldUnits / receivedUnits * 100).toFixed(1)}%`;
}
function carliContext(brand: string, stats: QuickStats, hasInventory: boolean) {
  if (!hasInventory) {
    return 'The brand is signed into the Manymoons recovery portal but has not added inventory yet. Help them understand what to send first, what data Carli needs, how recovery pricing works, and which channels might fit. Keep answers to two short paragraphs at most.';
  }
  return [`The brand is ${brand || 'a Manymoons brand'}, signed into their recovery portal.`, `Current position: ${stats.live} active units, ${stats.sold} units sold, ${stats.sales} recovery generated, ${stats.maxOpportunity} of remaining opportunity across ${stats.received} units received.`, 'Answer as their recovery analyst using these numbers. Be specific and commercial. Two short paragraphs at most.'].join(' ');
}